<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen && vehicle"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click.self="handleClose"
      >
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

        <div class="flex min-h-full items-center justify-center p-4">
          <div class="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-black">
            <div class="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-black">
              <div class="flex items-center">
                <div class="mr-3 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/20">
                  <svg class="h-5 w-5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-white">Edit Vehicle</h3>
              </div>

              <button
                type="button"
                @click="handleClose"
                class="text-gray-400 transition-colors duration-200 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form @submit.prevent="handleSubmit" class="px-6 py-4">
              <div class="mb-6">
                <h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Vehicle Information</h4>
                <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label for="vehicle_make" class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Vehicle Make <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="vehicle_make"
                      v-model="formData.vehicle_make"
                      type="text"
                      required
                      class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 dark:border-gray-600 dark:bg-black dark:text-white"
                      :style="inputStyle"
                    />
                    <p v-if="errors.vehicle_make" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.vehicle_make }}</p>
                  </div>

                  <div>
                    <label for="vehicle_model" class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Vehicle Model <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="vehicle_model"
                      v-model="formData.vehicle_model"
                      type="text"
                      required
                      class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 dark:border-gray-600 dark:bg-black dark:text-white"
                      :style="inputStyle"
                    />
                    <p v-if="errors.vehicle_model" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.vehicle_model }}</p>
                  </div>

                  <div>
                    <label for="vehicle_year" class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Vehicle Year <span class="text-red-500">*</span>
                    </label>
                    <select
                      id="vehicle_year"
                      v-model.number="formData.vehicle_year"
                      required
                      class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 dark:border-gray-600 dark:bg-black dark:text-white"
                      :style="inputStyle"
                    >
                      <option value="" disabled>Select year</option>
                      <option v-for="year in yearOptions" :key="year" :value="year">
                        {{ year }}
                      </option>
                    </select>
                    <p v-if="errors.vehicle_year" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.vehicle_year }}</p>
                  </div>

                  <div>
                    <label for="plate_number" class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Plate Number <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="plate_number"
                      v-model="formData.plate_number"
                      type="text"
                      required
                      class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 font-mono uppercase text-gray-900 dark:border-gray-600 dark:bg-black dark:text-white"
                      :style="inputStyle"
                    />
                    <p v-if="errors.plate_number" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.plate_number }}</p>
                  </div>
                </div>
              </div>

              <div class="sticky bottom-0 flex justify-end space-x-3 border-t border-gray-200 bg-white pt-4 dark:border-gray-700 dark:bg-black">
                <button
                  type="button"
                  @click="handleClose"
                  :disabled="loading"
                  class="rounded-lg bg-gray-200 px-4 py-2 font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-black dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  :disabled="loading"
                  class="flex items-center rounded-lg bg-amber-600 px-4 py-2 font-medium text-white transition-colors duration-200 hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <svg v-if="loading" class="-ml-1 mr-2 h-4 w-4 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ loading ? 'Updating...' : 'Update Vehicle' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useBrand } from '@/composables/brand/useBrand';
import { vehicleService } from '@/services/api/vehicleService';
import type { Vehicle } from '@/types/vehicle';

const props = defineProps<{
  isOpen: boolean;
  vehicle: Vehicle | null;
}>();

const emit = defineEmits<{
  close: [];
  saved: [vehicle: Vehicle];
}>();

const toast = useToast();
const { brandColors } = useBrand();

const loading = ref(false);
const errors = ref<Record<string, string>>({});

const formData = ref({
  vehicle_make: '',
  vehicle_model: '',
  vehicle_year: 0,
  plate_number: '',
});

const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let year = currentYear + 1; year >= 1980; year--) {
    years.push(year);
  }
  return years;
});

const inputStyle = computed(() => ({
  borderColor: brandColors.value.primary,
  '--tw-ring-color': brandColors.value.primary,
  outline: 'none',
}));

watch(
  () => props.vehicle,
  (newVehicle) => {
    if (!newVehicle) {
      return;
    }

    formData.value = {
      vehicle_make: newVehicle.vehicle_make || '',
      vehicle_model: newVehicle.vehicle_model || '',
      vehicle_year: newVehicle.vehicle_year || 0,
      plate_number: newVehicle.plate_number || '',
    };
    errors.value = {};
  },
  { immediate: true }
);

async function handleSubmit() {
  errors.value = {};

  if (!formData.value.vehicle_make.trim()) {
    errors.value.vehicle_make = 'Vehicle make is required';
  }

  if (!formData.value.vehicle_model.trim()) {
    errors.value.vehicle_model = 'Vehicle model is required';
  }

  if (!formData.value.vehicle_year) {
    errors.value.vehicle_year = 'Vehicle year is required';
  }

  if (!formData.value.plate_number.trim()) {
    errors.value.plate_number = 'Plate number is required';
  }

  if (Object.keys(errors.value).length > 0) {
    toast.error('Please fix the errors before submitting');
    return;
  }

  if (!props.vehicle) return;

  loading.value = true;
  try {
    const updated = await vehicleService.updateVehicle(
      props.vehicle.vehicle_id,
      { ...formData.value },
      [],
      []
    );
    toast.success('Vehicle updated successfully');
    emit('saved', updated);
    emit('close');
  } catch (err: any) {
    toast.error(err.response?.data?.message || 'Failed to update vehicle');
  } finally {
    loading.value = false;
  }
}

function handleClose() {
  if (!loading.value) {
    emit('close');
  }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div > div,
.modal-leave-active > div > div {
  transition: transform 0.3s ease;
}

.modal-enter-from > div > div,
.modal-leave-to > div > div {
  transform: scale(0.95);
}
</style>
